// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

/**
 * Scanning Utility Functions
 * Pure functions for system scanning, discovery generation, and scan result calculations
 */

import GameConfig from '../../config/GameConfig';
import AlienManager from './AlienManager';

/**
 * Generate scan results for a star system
 * Includes discoveries, anomalies, planets, life signs, aliens, and credits
 * 
 * @param {Object} system - The star system being scanned
 * @param {Object} currentLocation - Current ship location for distance calculations
 * @param {number} distanceFromHere - Distance from current location to system
 * @param {number} sensorHealth - Sensor system health (0-100)
 * @param {number} sensorUpgradeBonus - Sensor upgrade bonus (0-1)
 * @param {Function} addFuel - Function to add fuel to ship
 * @param {Function} addCredits - Function to add credits
 * @param {boolean} isRescan - Whether this is a rescan of previously scanned system
 * @param {Object} storedResults - Previously stored scan results (for rescans)
 * @param {Object} universeAliens - Universe-specific alien configuration
 * @returns {Object} Scan results with discoveries, bonuses, aliens, and metadata
 */
export const generateScanResults = (
  system,
  currentLocation,
  distanceFromHere,
  sensorHealth,
  sensorUpgradeBonus = 0,
  addFuel,
  addCredits,
  isRescan = false,
  storedResults = null,
  universeAliens = null
) => {
  // If it's a rescan, we still generate fresh data but mark it as rescan (no new rewards)
  // This ensures alien data and other dynamic content is always current
  const isRescanFlag = isRescan && storedResults;
  
  // Calculate sensor damage penalty
  let sensorPenalty = 1.0;
  const penaltyConfig = GameConfig.scanning.sensorDamagePenalty;
  if (penaltyConfig.enabled) {
    if (sensorHealth < penaltyConfig.criticalThreshold) {
      sensorPenalty = penaltyConfig.criticalPenalty;
    } else if (sensorHealth < penaltyConfig.minorThreshold) {
      sensorPenalty = penaltyConfig.minorPenalty;
    }
  }
  
  // Calculate threat multiplier
  const threatMultiplier = GameConfig.scanning.threatMultipliers[system.threat] || 1.0;
  
  // Calculate distance bonus based on distance from current location (capped)
  const distanceBonus = Math.min(
    (distanceFromHere / 100) * GameConfig.scanning.distanceBonusPerHundredLY,
    GameConfig.scanning.maxDistanceBonus
  );
  
  // Calculate total credits with all multipliers
  const baseCredits = Math.floor(
    Math.random() * (GameConfig.scanning.baseCreditsMax - GameConfig.scanning.baseCreditsMin + 1)
  ) + GameConfig.scanning.baseCreditsMin;
  
  const bonusCredits = Math.random() < GameConfig.scanning.bonusChance ?
    Math.floor(
      Math.random() * (GameConfig.scanning.bonusCreditsMax - GameConfig.scanning.bonusCreditsMin + 1)
    ) + GameConfig.scanning.bonusCreditsMin : 0;
  
  const totalCreditsBonus = Math.floor(
    (baseCredits + bonusCredits) * 
    threatMultiplier * 
    (1 + distanceBonus) * 
    (1 + sensorUpgradeBonus) * 
    sensorPenalty
  );
  
  // Generate detailed scan results with random discoveries
  const discoveries = [];
  const discoveryBonuses = [];
  
  // Random resource discoveries
  const resourceTypes = ['Dilithium Crystals', 'Antimatter Deposits', 'Rare Minerals', 'Exotic Matter', 'Ancient Artifacts'];
  const discConfig = GameConfig.scanning.discoveries;
  const resourceAmount = Math.floor(Math.random() * (discConfig.maxResources - discConfig.minResources + 1)) + discConfig.minResources;
  
  // Store discovery rewards to be applied later (not during render)
  const discoveryRewards = [];
  
  for (let i = 0; i < resourceAmount; i++) {
    const discovery = resourceTypes[Math.floor(Math.random() * resourceTypes.length)];
    discoveries.push(discovery);
    
    // Calculate discovery effects but don't apply them yet
    const effect = GameConfig.scanning.discoveryEffects[discovery];
    if (effect) {
      if (effect.type === 'fuel') {
        const fuelBonus = Math.floor(Math.random() * (effect.maxValue - effect.minValue + 1)) + effect.minValue;
        discoveryRewards.push({ type: 'fuel', amount: fuelBonus });
        discoveryBonuses.push(`${discovery}: +${fuelBonus}% fuel`);
      } else if (effect.type === 'credits') {
        const creditsBonus = Math.floor(Math.random() * (effect.maxValue - effect.minValue + 1)) + effect.minValue;
        discoveryRewards.push({ type: 'credits', amount: creditsBonus });
        discoveryBonuses.push(`${discovery}: +${creditsBonus} credits`);
      } else if (effect.type === 'fuelEfficiency') {
        // Store for next warp (would need state management)
        discoveryBonuses.push(`${discovery}: Fuel efficiency boost`);
      }
    }
  }
  
  // Random anomalies
  const anomalies = [];
  const anomalyTypes = [
    { name: 'Temporal Distortion', color: '#ff00ff', danger: 'HIGH' },
    { name: 'Subspace Rift', color: '#ffaa00', danger: 'MODERATE' },
    { name: 'Ion Storm', color: '#00ffff', danger: 'LOW' },
    { name: 'Gravitational Anomaly', color: '#ff4444', danger: 'HIGH' },
    { name: 'Nebula Cloud', color: '#8888ff', danger: 'LOW' }
  ];
  
  if (Math.random() < discConfig.anomalyChance) {
    const anomaly = anomalyTypes[Math.floor(Math.random() * anomalyTypes.length)];
    anomalies.push(anomaly);
  }
  
  // Hidden planets
  const planetCount = Math.floor(Math.random() * (discConfig.maxPlanets - discConfig.minPlanets + 1)) + discConfig.minPlanets;
  const planetTypes = ['Class M (Habitable)', 'Gas Giant', 'Ice World', 'Desert', 'Ocean World', 'Volcanic'];
  const atmosphereTypes = ['Breathable', 'Toxic', 'Thin', 'Dense', 'None', 'Corrosive'];
  const planets = [];
  for (let i = 0; i < planetCount; i++) {
    planets.push({
      name: `${system.name}-${String.fromCharCode(65 + i)}`,
      type: planetTypes[Math.floor(Math.random() * planetTypes.length)],
      moons: Math.floor(Math.random() * 4),
      atmosphere: atmosphereTypes[Math.floor(Math.random() * atmosphereTypes.length)]
    });
  }
  
  // Life signs and aliens
  const lifeConfig = GameConfig.scanning.lifeSigns;
  const lifeSignChance = Math.random();
  let lifeSigns = 'None Detected';
  let lifeColor = '#888888';
  let alienInfo = null;
  let alienSpecies = [];
  
  // Get aliens for this planet from universe config
  if (universeAliens && system.id !== undefined) {
    alienSpecies = AlienManager.getAliensForPlanet(system.id, universeAliens);
    
    if (alienSpecies.length > 0) {
      alienInfo = AlienManager.formatAlienInfoForScan(alienSpecies);
      lifeSigns = 'Intelligent Life Detected!';
      lifeColor = '#00ff88';
    }
  }
  
  // If no aliens assigned, use random life signs
  if (!alienInfo) {
    if (lifeSignChance < lifeConfig.intelligentChance) {
      lifeSigns = 'Intelligent Life Detected!';
      lifeColor = '#00ff88';
    } else if (lifeSignChance < (lifeConfig.intelligentChance + lifeConfig.primitiveChance)) {
      lifeSigns = 'Primitive Life Forms';
      lifeColor = '#ffaa00';
    }
  }
  
  // Star types
  const starTypes = ['G-Type (Yellow Dwarf)', 'M-Type (Red Dwarf)', 'K-Type (Orange Dwarf)', 'F-Type (Yellow-White)', 'A-Type (White)', 'B-Type (Blue-White)'];
  const starType = starTypes[Math.floor(Math.random() * starTypes.length)];
  
  // Build scan notes
  let notes = `Scan complete. ${discoveries.length} resource signature${discoveries.length !== 1 ? 's' : ''} detected`;
  if (discoveries.length > 0) {
    notes += `: ${discoveries.join(', ')}`;
  }
  notes += `. Life signs: ${lifeSigns}.`;
  
  // Add alien information if present (ENHANCED)
  if (alienInfo) {
    notes += `\n\n🧬 ALIEN SPECIES: ${alienInfo}`;
  }
  
  if (anomalies.length > 0) {
    notes += `\n\n⚠️ Warning: ${anomalies[0].name} detected (${anomalies[0].danger} danger).`;
  }
  
  // Add services information (ENHANCED)
  if (system.services && system.services.length > 0) {
    const serviceNames = {
      'refuel': '⛽ Refuel',
      'trade': '💱 Trade',
      'weapons': '⚔️ Weapons',
      'upgrades': '⬆️ Upgrades',
      'modules': '🔧 Modules',
      'exploration': '🌍 Exploration',
      'repairs': '🔨 Repairs'
    };
    const availableServices = system.services.map(s => serviceNames[s] || s).join(', ');
    notes += `\n\n🏪 AVAILABLE SERVICES: ${availableServices}`;
  }
  
  if (discoveryBonuses.length > 0) {
    notes += `\n\n✨ Bonuses: ${discoveryBonuses.join(', ')}.`;
  }
  
  // On rescan, show no credits awarded
  if (isRescanFlag) {
    notes += `\n\n💰 Credits awarded: 0 (re-scan).`;
  } else {
    notes += `\n\n💰 Credits awarded: ${totalCreditsBonus}.`;
  }
  
  return {
    discoveries,
    discoveryBonuses: isRescanFlag ? [] : discoveryBonuses, // No bonuses on rescan
    discoveryRewards: isRescanFlag ? [] : discoveryRewards, // Rewards to be applied in useEffect
    anomalies,
    planets,
    lifeSigns,
    lifeColor,
    alienInfo,
    alienSpecies,
    creditsBonus: isRescanFlag ? 0 : totalCreditsBonus, // No credits on rescan
    hiddenRoute: Math.random() < discConfig.hiddenRouteChance,
    isRescan: isRescanFlag, // Mark as rescan if applicable
    // Data needed by SystemScanner
    starType,
    resources: system.resources,
    threat: system.threat,
    notes,
    // Multipliers for display
    multipliers: {
      threat: threatMultiplier,
      distance: 1 + distanceBonus,
      sensorUpgrade: 1 + sensorUpgradeBonus,
      sensorDamage: sensorPenalty
    }
  };
};

/**
 * Check if a system has been scanned before
 * @param {number} systemId - System ID to check
 * @param {Set} scannedSystems - Set of scanned system IDs
 * @returns {boolean} True if system has been scanned
 */
export const isSystemScanned = (systemId, scannedSystems) => {
  return scannedSystems.has(systemId);
};

/**
 * Calculate scan fuel cost
 * @returns {number} Fuel cost percentage
 */
export const getScanFuelCost = () => {
  return GameConfig.scanning.fuelCost;
};

/**
 * Check if ship has enough fuel to scan
 * @param {number} currentFuel - Current fuel percentage
 * @returns {boolean} True if enough fuel
 */
export const canAffordScan = (currentFuel) => {
  return currentFuel >= GameConfig.scanning.fuelCost;
};
